import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  FolderOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { FolderSLAOverallMetrics } from "../types/folderSla.types";

interface FolderSLASummaryCardsProps {
  data: FolderSLAOverallMetrics | null;
  loading?: boolean;
}

const FolderSLASummaryCards: React.FC<FolderSLASummaryCardsProps> = ({
  data,
  loading = false,
}) => {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
            <div className="flex items-start">
              <div className="rounded-md p-3 bg-gray-200 w-12 h-12" />
              <div className="ml-4 flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Safe accessor with default value
  const safeNumber = (value: number | undefined | null, defaultValue = 0): number => {
    return typeof value === "number" && !isNaN(value) ? value : defaultValue;
  };

  // Map API fields to display values
  const overallCompliancePct = safeNumber(data.overall_compliance_pct);
  const beforeCutoffCompliancePct = safeNumber(data.before_cutoff_compliance_pct);
  const afterCutoffCompliancePct = safeNumber(data.after_cutoff_compliance_pct);
  const compliantBeforeCutoff = safeNumber(data.compliant_before_cutoff);
  const compliantAfterCutoff = safeNumber(data.compliant_after_cutoff);
  const totalProcessed = safeNumber(data.total_processed);
  const totalCompliant = safeNumber(data.total_compliant);
  const totalNonCompliant = safeNumber(data.total_non_compliant);
  const avgBusinessHours = safeNumber(data.avg_completion_time_business_hours);

  // Calculate non-compliant percentage
  const nonCompliantPct = totalProcessed > 0
    ? (totalNonCompliant / totalProcessed) * 100
    : 0;

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 90) return "bg-green-100 text-green-600";
    if (percentage >= 80) return "bg-yellow-100 text-yellow-600";
    if (percentage >= 70) return "bg-orange-100 text-orange-600";
    return "bg-red-100 text-red-600";
  };

  const cardItems = [
    {
      title: "Cumplimiento General",
      value: `${overallCompliancePct.toFixed(1)}%`,
      icon: <CheckCircleOutlined className="text-2xl" />,
      color: getComplianceColor(overallCompliancePct),
      subtitle: `${totalCompliant}/${totalProcessed} expedientes`,
    },
    {
      title: "Antes del Corte",
      value: `${beforeCutoffCompliancePct.toFixed(1)}%`,
      icon: <ClockCircleOutlined className="text-2xl" />,
      color: "bg-green-100 text-green-600",
      subtitle: `${compliantBeforeCutoff} expedientes`,
    },
    {
      title: "Después del Corte",
      value: `${afterCutoffCompliancePct.toFixed(1)}%`,
      icon: <ClockCircleOutlined className="text-2xl" />,
      color: "bg-blue-100 text-blue-600",
      subtitle: `${compliantAfterCutoff} expedientes`,
    },
    {
      title: "Fuera de SLA",
      value: `${nonCompliantPct.toFixed(1)}%`,
      icon: <WarningOutlined className="text-2xl" />,
      color:
        nonCompliantPct > 10
          ? "bg-red-100 text-red-600"
          : "bg-gray-100 text-gray-600",
      subtitle: `${totalNonCompliant} expedientes`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cardItems.map((item, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start">
              <div className={`rounded-md p-3 ${item.color}`}>{item.icon}</div>
              <div className="ml-4 flex-1">
                <p className="text-sm text-gray-500 mb-1">{item.title}</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {item.value}
                </p>
                <p className="text-xs text-gray-500">{item.subtitle}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional summary row */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="flex items-center justify-center mb-2">
              <FolderOutlined className="text-xl text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {totalProcessed.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Total Procesados</div>
          </div>
          <div>
            <div className="flex items-center justify-center mb-2">
              <CheckCircleOutlined className="text-xl text-green-500" />
            </div>
            <div className="text-2xl font-bold text-green-600">
              {totalCompliant.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">En SLA</div>
          </div>
          <div>
            <div className="flex items-center justify-center mb-2">
              <WarningOutlined className="text-xl text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-600">
              {totalNonCompliant.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Fuera de SLA</div>
          </div>
          <div>
            <div className="flex items-center justify-center mb-2">
              <FieldTimeOutlined className="text-xl text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {avgBusinessHours.toFixed(1)}h
            </div>
            <div className="text-sm text-gray-500">Tiempo Promedio</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FolderSLASummaryCards;
